import React, { useState } from 'react';
import { AvatarProps as MuiAvatarProps } from '@mui/material';
import { StyledAvatar, StyledBadge } from './Avatar.style';
const FIRST_INITIAL_COLORS = {
  a: "#CFEEFC",
  b: "#CFE1FC",
  c: "#E1DEFF",
  d: "#F2D7F1",
  e: "#FBCBEB",
  f: "#FFC8C1",
  g: "#FFDEDE",
  h: "#FCE0CF",
  i: "#FFF2C5",
  j: "#F3F8BE",
  k: "#E4F2C6",
  l: "#99D8EC",
  m: "#99C4EC",
  n: "#D9F3DC",
  o: "#C3BDFF",
  p: "#C3EBE6",
  q: "#E9B9E7",
  r: "#EAEFF1",
  s: "#DD9BC7",
  t: "#F1EEEA",
  u: "#F49D97",
  v: "#F1F1E2",
  w: "#EFB7B7",
  x: "#E2EDE7",
  y: "#F7E4A1",
  z: "#EAE2ED",
};
const DEFAULT_AVATAR_COLOR = "#99D8EC";
export interface AvatarProps extends Omit<MuiAvatarProps, 'variant'> {
  /**
   * The source URL for the avatar image
   */
  src?: string;
  /**
   * Alternative text for the avatar image (should contain the username)
   */
  alt?: string;
  /**
   * The size of the avatar
   */
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  /**
   * The variant of the avatar
   */
  variant?: 'circular' | 'rounded' | 'square';
  /**
   * The color of the avatar background
   */
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default';
  /**
   * Whether the avatar should have a border
   */
  bordered?: boolean;
  /**
   * Whether the avatar should be clickable
   */
  clickable?: boolean;
  /**
   * Whether to show the badge (green when true, gray when false)
   */
  badgeOn?: boolean;
  /**
   * Callback fired when the avatar is clicked
   */
  onClick?: () => void;
  /**
   * The content to display inside the avatar (usually initials)
   */
  children?: React.ReactNode;
}
export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  size = 'medium',
  variant = 'circular',
  color = 'default',
  bordered = false,
  clickable = false,
  badgeOn = false,
  onClick,
  children,
  ...props
}) => {
  const [imageError, setImageError] = useState(false);
  const handleImageError = () => {
    setImageError(true);
  };
  const getFirstInitial = (name?: string): string => {
    if (!name) return '';
    return name.charAt(0).toUpperCase();
  };
  const getInitialBackgroundColor = (name?: string): string => {
    if (!name) return DEFAULT_AVATAR_COLOR;
    const firstLetter = name.charAt(0).toLowerCase() as keyof typeof FIRST_INITIAL_COLORS;
    return FIRST_INITIAL_COLORS[firstLetter] || DEFAULT_AVATAR_COLOR;
  };
  const shouldShowImage = src && !imageError;
  const firstInitial = getFirstInitial(alt);
  const initialBackgroundColor = getInitialBackgroundColor(alt);
  const avatarElement = (
    <StyledAvatar
      src={shouldShowImage ? src : undefined}
      alt={alt}
      variant={variant}
      size={size}
      color={color}
      bordered={bordered}
      clickable={clickable}
      onClick={onClick}
      onError={handleImageError}
      customBackgroundColor={!shouldShowImage ? `${initialBackgroundColor} !important` : undefined}
      {...props}
    >
      {!shouldShowImage && (children || firstInitial)}
    </StyledAvatar>
  );
  if (badgeOn) {
    return (
      <StyledBadge
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        variant="dot"
        overlap="circular"
        badgeOn={badgeOn}
      >
        {avatarElement}
      </StyledBadge>
    );
  }
  return avatarElement;
}; 